<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import BrowseGoalCard from '@/goals/BrowseGoalCard.vue'
import { useContentProfileStore } from '@/contentprofile/contentprofile.store'
import { getGoalSubjectLabelFromValue } from '@/contentprofile/no_sel_composable'
import { groupGoalHitsBySubject } from '@/browse/goals/goal_results'
import type { GoalHit } from '@/goals/goal_types'
import type { BrowseGoalFilters, GoalCardRestriction, StandardsByLongRef } from '@/goals/browse_goal_card'

const props = withDefaults(defineProps<{
  goals: GoalHit[]
  groupBySubject: boolean
  selectedSubject: string
  relatedHeadingLevel?: 2 | 3
  restrictionType?: GoalCardRestriction
  algoliaQueryId: string
  standardsByLongRef: StandardsByLongRef
  browseFilters: BrowseGoalFilters
}>(), { relatedHeadingLevel: 2, restrictionType: '' })

const emit = defineEmits<{ relatedSubject: [subject: string] }>()

const contentProfileStore = useContentProfileStore()
const { publishedSubjects } = storeToRefs(contentProfileStore)

const profileSubjectNames = computed(() => publishedSubjects.value.map(({ subject }: { subject: string }) => subject))

const groups = computed(() => {
  if (!props.groupBySubject) {
    return [{ subject: '', goals: props.goals }]
  }
  const { orderedSubjects, resultsBySubject } = groupGoalHitsBySubject(props.goals, {
    profileSubjects: profileSubjectNames.value,
    selectedSubject: props.selectedSubject
  })
  return orderedSubjects.map((subject) => ({ subject, goals: resultsBySubject[subject] }))
})

function cardStandardSets(goal: GoalHit) {
  const subject = getGoalSubjectLabelFromValue(goal.primary_subject)
  return {
    standardSetLabel: contentProfileStore.standardSetLabelForSubjectCard(subject),
    standardSetForRefDisplay: contentProfileStore.dropdownStandardSetForSubject(subject)
  }
}

// An ungrouped list has no subject, so it is never a related group.
function isRelatedGroup(groupSubject: string): boolean {
  const isGrouped = Boolean(groupSubject)
  const isSubjectSelected = Boolean(props.selectedSubject)
  return isGrouped && isSubjectSelected && groupSubject !== props.selectedSubject
}

const isPageLevelHeading = computed(() => props.relatedHeadingLevel === 2)
const headingClass = computed(() => (isPageLevelHeading.value ? 'text-display-sm text-gray-900' : 'text-text-2xl text-gray-700'))
const subjectLinkClass = computed(() => (isPageLevelHeading.value ? 'text-blue-400' : 'text-blue-600'))
</script>

<template>
  <div v-for="group in groups" :key="group.subject" :class="{ 'mb-6': groupBySubject }">
    <component
      :is="`h${relatedHeadingLevel}`"
      v-if="isRelatedGroup(group.subject)"
      class="mb-6 mt-1"
      :class="headingClass"
    >
      Related content in
      <button
        type="button"
        class="underline"
        :class="subjectLinkClass"
        @click="emit('relatedSubject', group.subject)"
      >
        {{ group.subject }}
      </button>
    </component>
    <ul class="grid gap-x-2 gap-y-5 min-[992px]:grid-cols-3" :aria-label="groupBySubject ? `${group.subject} goals` : undefined">
      <li v-for="(goal, index) in group.goals" :key="goal.uuid">
        <BrowseGoalCard
          :goal="goal"
          :position="index"
          :restriction-type="restrictionType"
          :algolia-query-id="algoliaQueryId"
          :standards-by-long-ref="standardsByLongRef"
          :browse-filters="browseFilters"
          v-bind="cardStandardSets(goal)"
        />
      </li>
    </ul>
  </div>
</template>
